import {
  DNDContainerSlotProps,
  DNDModifiers,
  DragAndDrop,
  DragAndDropProps,
} from '@chipinside/frontend'
import { DNDColumn } from '@chipinside/frontend/dist/DragAndDrop/Column'
import { DNDDefaultColumnProps } from '@chipinside/frontend/dist/DragAndDrop/Column/Default/types'
import { Translate } from '@chipinside/translate'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { useEffect } from 'react'

import { Icon } from '#/components/Icon'
import Colors from '#/styles/Colors'

type Option = { label: string; value: string }

type Props = {
  options: Option[]
  value?: Option['value'][]
  onChange?: (value: Props['value']) => void
  label?: string
  description?: string
}

/**
 * Pega valor inicial para o caso de não ter um valor definido, ordenando as colunas na ordem que estão definidas nas opções
 */
const getInitialValue = (options: Option[]) => options.map(option => option.value)

export const Sortable = ({ description, label, onChange, options, value }: Props) => {
  let columns: DNDColumn[] = options.map(option => ({ ...option, id: option.value }))

  if (value) {
    columns = value.map(id => columns.find(col => col.id === id)) as DNDColumn[]
  }

  useEffect(() => {
    if (!value) {
      onChange?.(getInitialValue(options))
    }
  }, [])

  const onDragEnd: DragAndDropProps['onDragEnd'] = ({ finalColumns }) => {
    onChange?.(finalColumns.map(column => column.id))
  }

  const Column = ({ column, handleRef }: DNDDefaultColumnProps) => {
    return (
      <Box
        sx={{
          alignItems: 'center',
          backgroundColor: Colors.feedback.regular.backgroundColor,
          display: 'flex',
          flex: 1,
          gap: 1.5,
          px: 2,
          py: 1.5,
        }}
      >
        <Icon
          name="grip-vertical"
          ref={handleRef}
          size={16}
          color={Colors.theme.colors.text.main}
        />
        <Typography variant="body2">{column.label}</Typography>
      </Box>
    )
  }

  const Container = ({ children, id }: DNDContainerSlotProps) => (
    <Stack
      divider={<Divider />}
      id={id}
      sx={{
        backgroundColor: Colors.feedback.regular.backgroundColor,
        borderRadius: 1,
        overflow: 'auto',
      }}
    >
      {children}
    </Stack>
  )

  const Label = () => (
    <Typography variant="h6" sx={{ fontWeight: 600 }}>
      {label ?? Translate({ messageKey: 'column_order' })}
    </Typography>
  )

  const Description = () => (
    <Box sx={{ alignItems: 'center', display: 'flex', gap: 0.5 }}>
      <Icon name="arrow-up-down" size={12} color={Colors.feedback.neutral.color} />
      <Typography variant="caption" color={Colors.feedback.neutral.color}>
        {description ?? Translate({ messageKey: 'dnd_drag_to_define_order' })}
      </Typography>
    </Box>
  )

  const columnsConfig: DragAndDropProps['columnsConfig'] = {
    modifiers: [DNDModifiers.RestrictToWindow],
    slots: {
      render: Column,
    },
    sortable: true,
  }

  const slots: DragAndDropProps['slots'] = {
    container: Container,
  }

  return (
    <Stack sx={{ gap: 1.5 }}>
      <Label />
      <Description />
      <DragAndDrop
        id="column-order"
        columns={columns}
        columnsConfig={columnsConfig}
        slots={slots}
        onDragEnd={onDragEnd}
      />
    </Stack>
  )
}
